import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocateToggles } from '../MachineRobotCleaningControls/hooks/useLocateToggles';
import { BasicModeInfoBanner, StyledMachineRobotBasicModeActions } from './MachineRobotBasicModeActions.styles';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { selectCustomerId } from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';
import { RobotCleaningControlActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSlice';
import { Cv50Commands } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

interface MachineRobotBasicModeActionsProps {
  /** The PRA device id the command needs is resolved from the material and serial number. */
  materialNumber: string;
  serialNumber: string;
}

// Basic mode has no map, so Beep and Blink locate the robot. Each is a two-state toggle that follows
// the device's reported settings.beep / settings.blink, and a press asks for the opposite state. They only change
// when the user presses them and the device reports the new state, so nothing is switched off when the panel closes.
export const MachineRobotBasicModeActions = ({
  materialNumber,
  serialNumber,
}: MachineRobotBasicModeActionsProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const { isBeepOn, isBlinkOn, pendingCommand, isLocked, toggle } = useLocateToggles({ materialNumber, serialNumber });

  // Beep / Blink follow the device's settings, which come with the paused-state query + subscription.
  useEffect(() => {
    if (customerId) {
      dispatch(
        RobotCleaningControlActions.subscribeToPausedState({ model: 'CV50', customerId, materialNumber, serialNumber })
      );
    }

    return (): void => {
      dispatch(RobotCleaningControlActions.unsubscribeFromPausedState());
    };
  }, [dispatch, customerId, materialNumber, serialNumber]);

  const [isAlertVisible, setIsAlertVisible] = useState(true);

  return (
    <StyledMachineRobotBasicModeActions className="machine-robot-basic-mode">
      {isAlertVisible && (
        <BasicModeInfoBanner role="status">
          <SvgIcon name="information" size={32} className="basic-mode-banner__info-icon" />
          <div className="basic-mode-banner__text">
            <p className="basic-mode-banner__title">{t('robotCleaningControl.basicMode.alertTitle')}</p>
            <p className="basic-mode-banner__description">{t('robotCleaningControl.basicMode.alertDescription')}</p>
          </div>
          <button
            type="button"
            className="basic-mode-banner__close"
            aria-label={t('common.close')}
            onClick={(): void => setIsAlertVisible(false)}
          >
            <SvgIcon name="close" size={24} />
          </button>
        </BasicModeInfoBanner>
      )}
      <div className="machine-robot-basic-mode__buttons">
        <KccButton
          type="tertiary"
          className={isBeepOn ? 'machine-robot-basic-mode__button--active' : undefined}
          aria-pressed={isBeepOn}
          disabled={isLocked}
          icon={pendingCommand === Cv50Commands.Beep ? <PendingSpinnerIcon /> : <SvgIcon name="beep" size={20} />}
          onClick={(): void => toggle(Cv50Commands.Beep, isBeepOn)}
        >
          {t('robotCleaningControl.beep.label')}
        </KccButton>
        <KccButton
          type="tertiary"
          className={isBlinkOn ? 'machine-robot-basic-mode__button--active' : undefined}
          aria-pressed={isBlinkOn}
          disabled={isLocked}
          icon={pendingCommand === Cv50Commands.Blink ? <PendingSpinnerIcon /> : <SvgIcon name="lightOn" size={20} />}
          onClick={(): void => toggle(Cv50Commands.Blink, isBlinkOn)}
        >
          {t('robotCleaningControl.blink.label')}
        </KccButton>
      </div>
    </StyledMachineRobotBasicModeActions>
  );
};
